import axios from 'axios';
import { API_URL } from '../config/api';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to attach Bearer token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for handling token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const rt = localStorage.getItem('refresh_token');
        const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, 
          rt ? { refresh_token: rt } : {},
          { 
            withCredentials: true,
            headers: rt ? { Authorization: `Bearer ${rt}` } : {}
          }
        );
        if (refreshRes.data.access_token) {
          localStorage.setItem('access_token', refreshRes.data.access_token);
          originalRequest.headers.Authorization = `Bearer ${refreshRes.data.access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Training modules
export const getModules = () => api.get('/modules');
export const getModule = (moduleId) => api.get(`/modules/${moduleId}`);
export const updateModuleProgress = (moduleId, sectionIndex) => 
  api.post(`/modules/${moduleId}/progress?section_index=${sectionIndex}`);
export const submitQuiz = (moduleId, answers) => 
  api.post(`/modules/${moduleId}/quiz`, { module_id: moduleId, answers });

// Certificates
export const downloadCertificate = async (moduleId) => {
  window.open(`${API_URL}/api/certificates/${moduleId}`, '_blank');
};

// Dashboard
export const getStaffDashboard = () => api.get('/dashboard/staff');
export const getAdminDashboard = () => api.get('/dashboard/admin');

// User management (Admin)
export const getUsers = () => api.get('/users');
export const createUser = (userData) => api.post('/users', userData);
export const updateUser = (userId, userData) => api.put(`/users/${userId}`, userData);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Reports (Admin)
export const getComplianceReport = (format = 'json') => api.get(`/reports/compliance?format=${format}`);
export const getUserReport = (userId) => api.get(`/reports/user/${userId}`);
export const downloadComplianceCSV = async () => {
  window.open(`${API_URL}/api/reports/compliance?format=csv`, '_blank');
};

// Notifications (Admin)
export const sendReminders = () => api.post('/notifications/send-reminders');

// Profile management
export const updateProfile = (data) => api.put('/profile', data);
export const changePassword = (data) => api.post('/profile/change-password', data);

// Staff self-registration
export const registerStaff = (data) => 
  axios.post(`${API_URL}/api/auth/register-staff`, data);

// Pending registrations (Admin)
export const getPendingUsers = () => api.get('/users/pending');
export const approveUser = (userId) => api.post(`/users/${userId}/approve`);
export const rejectUser = (userId) => api.post(`/users/${userId}/reject`);

// Onboarding - Staff
export const getOnboardingStatus = () => api.get('/onboarding/status');
export const getOnboardingSections = () => api.get('/onboarding/sections');
export const getOnboardingSection = (sectionId) => api.get(`/onboarding/section/${sectionId}`);
export const saveOnboardingSection = (sectionId, data, submit = false) => 
  api.post(`/onboarding/section/${sectionId}`, { data, submit });
export const uploadOnboardingDocument = (sectionId, file, category = 'general') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  return api.post(`/onboarding/section/${sectionId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteOnboardingDocument = (fileId) => api.delete(`/onboarding/document/${fileId}`);
export const downloadOnboardingDocument = async (fileId, filename) => {
  window.open(`${API_URL}/api/onboarding/document/${fileId}/download`, '_blank');
};

// Onboarding - Admin
export const getOnboardingOverview = () => api.get('/onboarding/admin/overview');
export const getStaffOnboarding = (userId) => api.get(`/onboarding/admin/staff/${userId}`);
export const approveOnboardingSection = (userId, sectionId, notes = '') => 
  api.post(`/onboarding/admin/staff/${userId}/section/${sectionId}/approve`, { notes });
export const rejectOnboardingSection = (userId, sectionId, notes = '') => 
  api.post(`/onboarding/admin/staff/${userId}/section/${sectionId}/reject`, { notes });
export const fullyApproveStaff = (userId) => 
  api.post(`/onboarding/admin/staff/${userId}/fully-approve`);
export const approveAllSections = (userId) => 
  api.post(`/onboarding/admin/staff/${userId}/approve-all-sections`);
export const getOnboardingCompliance = () => api.get('/onboarding/admin/compliance');
export const getOnboardingStats = () => api.get('/onboarding/admin/onboarding-stats');
export const getSingleCentralRecord = () => api.get('/onboarding/admin/scr');
export const exportStaffOnboardingCSV = async (userId, staffName) => {
  window.open(`${API_URL}/api/onboarding/admin/staff/${userId}/export-csv`, '_blank');
};

// Scheduling & Pay Config
export const getAdminTutors = () => api.get('/scheduling/admin/tutors');
export const updateTutorConfig = (userId, data) => api.put(`/scheduling/admin/tutor/${userId}/config`, data);

// Contract Templates (Admin)
export const getContractTemplates = () => api.get('/scheduling/admin/contract-templates');
export const assignContract = (data) => api.post('/scheduling/admin/assign-contract', data);
export const uploadContractTemplate = (file, templateType, templateName) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('template_type', templateType);
  formData.append('template_name', templateName);
  return api.post('/scheduling/admin/contract-template', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteContractTemplate = (fileId) => api.delete(`/scheduling/admin/contract-template/${fileId}`);
export const downloadContractTemplate = async (fileId, filename) => {
  window.open(`${API_URL}/api/scheduling/admin/contract-template/${fileId}/download`, '_blank');
};

// Contracts (Admin)
export const getAdminContracts = () => api.get('/scheduling/admin/contracts');
export const getAdminContractDetails = (userId) => api.get(`/scheduling/admin/contract/${userId}/details`);
export const amendContract = (userId, data) => api.post(`/scheduling/admin/contract/${userId}/amend`, data);
export const downloadSignedContractAdmin = async (userId, name) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_URL}/api/scheduling/admin/contract/${userId}/signed-pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Signed_Contract_${name.replace(/\s+/g, '_')}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};
export const downloadSignedContractTutor = async () => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_URL}/api/scheduling/contract/signed-pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'My_Signed_Contract.pdf';
  a.click();
  window.URL.revokeObjectURL(url);
};

// Contracts (Tutor)
export const getMyContract = () => api.get('/scheduling/contract');
export const signContract = (signatureName) => api.post('/scheduling/contract/sign', { signature_name: signatureName });
export const downloadMyContractTemplate = async () => {
  window.open(`${API_URL}/api/scheduling/contract/download-template`, '_blank');
};

// Compliance check
export const getComplianceCheck = () => api.get('/scheduling/compliance-check');

// Scheduling - Admin
export const getSchedulingSettings = () => api.get('/scheduling/admin/settings');
export const updateSchedulingSettings = (data) => api.put('/scheduling/admin/settings', data);
export const createSchedule = (data) => api.post('/scheduling/admin/schedule', data);
export const getAdminSchedules = (weekStart) => api.get(`/scheduling/admin/schedules?week_start=${weekStart}`);
export const getScheduleOverview = (weekStart) => api.get(`/scheduling/admin/schedule-overview?week_start=${weekStart}`);
export const addSessionToSchedule = (scheduleId, data) => api.post(`/scheduling/admin/schedule/${scheduleId}/add-session`, data);
export const removeSession = (scheduleId, sessionId) => api.delete(`/scheduling/admin/schedule/${scheduleId}/session/${sessionId}`);
export const deleteSchedule = (scheduleId) => api.delete(`/scheduling/admin/schedule/${scheduleId}`);

// Scheduling - Tutor
export const getMySchedule = (weekStart) => api.get(`/scheduling/my-schedule?week_start=${weekStart}`);
export const confirmSessions = (data) => api.post('/scheduling/confirm-sessions', data);
export const confirmAllSessions = (scheduleId) => api.post('/scheduling/confirm-all', { schedule_id: scheduleId });
export const declineAllSessions = (scheduleId) => api.post('/scheduling/decline-all', { schedule_id: scheduleId });

// Standby Cover - Tutor
export const getAvailableShifts = (weekStart) => api.get(`/scheduling/available-shifts?week_start=${weekStart}`);
export const requestShift = (data) => api.post('/scheduling/request-shift', data);
export const getMyRequests = (weekStart) => api.get(`/scheduling/my-requests?week_start=${weekStart}`);

// Standby Cover - Admin
export const getStandbyRequests = (weekStart) => api.get(`/scheduling/admin/standby-requests?week_start=${weekStart}`);
export const getReleasedHours = (weekStart) => api.get(`/scheduling/admin/released-hours?week_start=${weekStart}`);
export const approveStandbyRequest = (requestId) => api.post(`/scheduling/admin/standby-request/${requestId}/approve`);
export const rejectStandbyRequest = (requestId, reason) => api.post(`/scheduling/admin/standby-request/${requestId}/reject`, { reason });
export const verifyHours = (scheduleId, sessionIds) => api.post(`/scheduling/admin/verify-hours/${scheduleId}`, { session_ids: sessionIds || [] });

// Timesheets - Admin
export const getAdminTimesheets = (month) => api.get(`/scheduling/admin/timesheets?month=${month}`);
export const generateTimesheets = (month) => api.post('/scheduling/admin/timesheets/generate', { month });
export const lockTimesheet = (id) => api.post(`/scheduling/admin/timesheet/${id}/lock`);
export const unlockTimesheet = (id) => api.post(`/scheduling/admin/timesheet/${id}/unlock`);
export const hideTimesheet = (id) => api.post(`/scheduling/admin/timesheet/${id}/hide`);
export const unhideTimesheet = (id) => api.post(`/scheduling/admin/timesheet/${id}/unhide`);

// Invoices - Admin
export const getAdminInvoices = (month) => api.get(`/scheduling/admin/invoices?month=${month}`);
export const generateInvoices = (month) => api.post('/scheduling/admin/invoices/generate', { month });
export const markInvoicePaid = (id) => api.post(`/scheduling/admin/invoice/${id}/mark-paid`);
export const markInvoiceUnpaid = (id) => api.post(`/scheduling/admin/invoice/${id}/mark-unpaid`);
export const addInvoiceExtra = (id, data) => api.post(`/scheduling/admin/invoice/${id}/add-extra`, data);
export const removeInvoiceExtra = (invoiceId, extraId) => api.delete(`/scheduling/admin/invoice/${invoiceId}/extra/${extraId}`);
export const downloadInvoicePdf = async (id, invoiceNumber) => {
  window.open(`${API_URL}/api/scheduling/admin/invoice/${id}/pdf`, '_blank');
};
export const getPayrollPreview = (month) => api.get(`/scheduling/admin/payroll-preview?month=${month}`);

// Dashboard Summaries
export const getAdminDashboardSummary = () => api.get('/scheduling/admin/dashboard-summary');
export const getMyDashboardSummary = () => api.get('/scheduling/my-dashboard-summary');

// Tutor Reliability
export const getTutorReliability = () => api.get('/scheduling/admin/tutor-reliability');

// Timesheets & Invoices - Tutor
export const getMyTimesheet = (month) => api.get(`/scheduling/my-timesheet?month=${month}`);
export const getMyInvoices = () => api.get('/scheduling/my-invoices');
export const downloadMyInvoicePdf = async (id, invoiceNumber) => {
  window.open(`${API_URL}/api/scheduling/my-invoice/${id}/pdf`, '_blank');
};

export default api;
